import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { concatMap, withLatestFrom } from 'rxjs/operators';
import * as appActions from '../actions/app.action';
import * as contactsActions from '../actions/contacts.action';
import * as iqObservationsActions from '../actions/get-iq-observations.action';
import * as invoiceAttachmentsActions from '../actions/invoice-attachments.action';
import * as invoicesActions from '../actions/invoices.action';
import * as ordersActions from '../actions/orders.action';
import * as paymentsActions from '../actions/payments.action';
import * as portalRolesActions from '../actions/portal-roles.action';
import * as serviceActions from '../actions/services.action';
import * as sofActions from '../actions/sof.action';
import * as ticketsActions from '../actions/tickets.action';
import { getContactsLoaded } from '../selectors/contacts.selector';
import { getInvoiceAttachmentsLoaded } from '../selectors/invoice-attachments.selector';
import { getInvoicesLoaded } from '../selectors/invoices.selector';
import { getOrdersLoaded } from '../selectors/orders.selector';
import { getPaymentsLoaded } from '../selectors/payments.selector';
import { getPortalRolesLoaded } from '../selectors/portal-roles.selector';
import { getServicesLoaded } from '../selectors/services.selector';
import { getSOFLoaded } from '../selectors/sof.selector';
import { getTicketsLoaded } from '../selectors/tickets.selector';

@Injectable()
export class AppEffects {
  private actions$ = inject(Actions);

  private store = inject(Store);

  // Reloads whatever was already loaded for the previous account so pages
  // don't keep showing stale/cleared data after an account switch.
  resetAccountData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(appActions.RESET_ACCOUNT_DATA),
      withLatestFrom(
        this.store.select(getOrdersLoaded),
        this.store.select(getServicesLoaded),
        this.store.select(getTicketsLoaded),
        this.store.select(getSOFLoaded),
        this.store.select(getPaymentsLoaded),
        this.store.select(getInvoicesLoaded),
        this.store.select(getInvoiceAttachmentsLoaded),
        this.store.select(getPortalRolesLoaded),
        this.store.select(getContactsLoaded),
      ),
      concatMap(
        ([
          ,
          ordersLoaded,
          servicesLoaded,
          ticketsLoaded,
          sofLoaded,
          paymentsLoaded,
          invoicesLoaded,
          invoiceAttachmentsLoaded,
          portalRolesLoaded,
          contactsLoaded,
        ]) =>
          of(
            new ordersActions.ClearOrders(),
            new serviceActions.ClearServices(),
            new ticketsActions.ClearTickets(),
            new sofActions.ClearSOF(),
            new paymentsActions.ClearPayments(),
            new invoicesActions.ClearInvoices(),
            new invoiceAttachmentsActions.ClearInvoiceAttachments(),
            new portalRolesActions.ClearPortalRoles(),
            new contactsActions.ClearContacts(),
            new iqObservationsActions.ClearIQObservations(),
            ...(ordersLoaded ? [new ordersActions.LoadOrders()] : []),
            ...(servicesLoaded ? [new serviceActions.LoadServices()] : []),
            ...(ticketsLoaded ? [new ticketsActions.LoadTickets()] : []),
            ...(sofLoaded ? [new sofActions.LoadServiceOrderForms()] : []),
            ...(paymentsLoaded ? [new paymentsActions.LoadPayments()] : []),
            ...(invoicesLoaded ? [new invoicesActions.LoadInvoices()] : []),
            ...(invoiceAttachmentsLoaded
              ? [new invoiceAttachmentsActions.LoadInvoiceAttachments()]
              : []),
            ...(portalRolesLoaded
              ? [new portalRolesActions.LoadPortalRoles()]
              : []),
            ...(contactsLoaded ? [new contactsActions.LoadContacts()] : []),
          ),
      ),
    ),
  );
}

